import { h, replaceContent } from '../lib/dom.js';
import { money, pct } from '../lib/format.js';
import { calculateFeaso } from '../../shared/feaso.js';
import { scoreListing } from '../../shared/scoring.js';
import { TIER2_DEFAULTS } from '../../shared/defaults.js';
import { saveListing, getDefaultOverrides } from '../lib/storage.js';
import { navigate } from '../lib/router.js';
import { lookupSappa } from '../lib/sappa.js';
import { scrapeListing } from '../lib/scrape.js';
import { estimateFrontage } from '../../shared/geometry.js';

const EMPTY = () => ({
  address: '',
  url: '',
  price: undefined,
  land_m2: undefined,
  frontage_m: undefined,
  zone: '',
  is_corner: false,
  target_dwellings: 3,
  sale_price_per_dwelling: undefined,
  notes: '',
  scrape: null,
  scrape_state: 'idle',    // idle | loading | done | error
  scrape_error: null,
  sappa: null,
  sappa_state: 'idle',
  sappa_error: null,
});

export function FinderNewPage() {
  const state = EMPTY();
  const formEl = h('form', {
    class: 'space-y-8',
    onsubmit: (e) => { e.preventDefault(); onSave(); },
  });
  const preview = h('aside', { class: 'space-y-6' });

  const parcelShape = () => (state.zone || state.is_corner || state.sappa)
    ? {
      zone: state.zone || null,
      is_corner: state.is_corner,
      area_m2: state.land_m2,
      frontage_m: state.frontage_m,
      // overlay_summary is the flattened per-parcel TNV summary (min site,
      // min frontage, etc). scoring.js looks for exactly these keys.
      overlays: state.sappa?.overlay_summary ?? {},
    }
    : null;

  const recompute = () => {
    if (!state.price || !state.sale_price_per_dwelling) {
      replaceContent(preview, prePanel());
      return;
    }
    const libraryOverrides = getDefaultOverrides();
    const feaso = calculateFeaso({
      purchase_price: state.price,
      dwellings: state.target_dwellings,
      sale_price_per_dwelling: state.sale_price_per_dwelling,
      dwelling_m2: TIER2_DEFAULTS.dwelling_m2,
      cost_per_m2: TIER2_DEFAULTS.cost_per_m2,
      overrides: libraryOverrides,
    });
    const scored = scoreListing({
      listing: {
        price: state.price,
        land_m2: state.land_m2,
        frontage_m: state.frontage_m,
      },
      parcel: parcelShape(),
      feaso: { profit_on_cost_pct: feaso.profit_on_cost_pct },
      targetDwellings: state.target_dwellings,
    });
    replaceContent(preview, scorePanel(scored, feaso));
  };

  const renderForm = () => {
    replaceContent(formEl, ...formBody(state, onChange, onLookup, onScrapeUrl));
  };

  const onChange = (patch) => {
    Object.assign(state, patch);
    recompute();
    // Don't renderForm() here — it would destroy + recreate inputs on every
    // keystroke, costing focus. Only rerun renderForm when the SAPPA lookup
    // finishes (below) and needs to populate several fields at once.
  };

  const onScrapeUrl = async (rawUrl) => {
    const url = rawUrl?.trim();
    if (!url) return;
    state.url = url;
    state.scrape_state = 'loading';
    state.scrape_error = null;
    renderForm();
    try {
      const data = await scrapeListing(url);
      state.scrape = data;
      state.scrape_state = data._partial ? 'error' : 'done';
      if (data._partial) {
        state.scrape_error = data._reason ?? 'could not parse the listing';
        // If we got the suburb from the URL, prefill a stub so the user
        // only has to type the street number and name.
        if (data.suburb && !state.address) {
          state.address = `, ${data.suburb} SA`;
        }
      }
      // Merge scraped fields into state — user overrides anything already filled.
      if (data.address && !state.address) state.address = data.address;
      if (data.price_amount != null && !state.price) state.price = data.price_amount;
      if (data.land_m2 != null && !state.land_m2) state.land_m2 = data.land_m2;
      renderForm();
      // Focus the address field so the user can type immediately if we
      // got a partial result.
      if (data._partial) {
        const input = formEl.querySelector('input[type="text"]');
        if (input) {
          input.focus();
          // Put the caret before the prefilled ", Suburb SA" so typing starts there.
          const pos = input.value.indexOf(',');
          if (pos > 0) input.setSelectionRange(0, 0);
        }
      }
      // Auto-trigger SAPPA as soon as we have a good address.
      if (data.address && !data._partial) {
        await onLookup();
      }
    } catch (err) {
      state.scrape_state = 'error';
      state.scrape_error = err.message;
      renderForm();
    }
  };

  const onLookup = async () => {
    if (!state.address?.trim()) return;
    state.sappa_state = 'loading';
    state.sappa_error = null;
    renderForm();
    try {
      const data = await lookupSappa(state.address);
      if (!data.parcel) {
        state.sappa_state = 'error';
        state.sappa_error = 'No parcel found for this address.';
      } else {
        state.sappa = data;
        state.sappa_state = 'done';
        state.address = data.match ?? state.address;
        if (data.parcel.area_m2 != null) state.land_m2 = Math.round(data.parcel.area_m2);
        if (data.parcel.is_corner != null) state.is_corner = !!data.parcel.is_corner;
        if (data.zone?.name) state.zone = data.zone.name;
        // Estimate frontage from the polygon's shortest edge, but only if
        // the user hasn't typed one themselves.
        if (state.frontage_m == null && data.parcel.polygon_wgs84) {
          const f = estimateFrontage(data.parcel.polygon_wgs84);
          if (f) state.frontage_m = f.estimate;
        }
      }
    } catch (err) {
      state.sappa_state = 'error';
      state.sappa_error = err.message;
    }
    recompute();
    renderForm();
  };

  const onSave = () => {
    const missing = [];
    if (!state.address?.trim()) missing.push('address');
    if (!state.price) missing.push('asking price');
    if (!state.sale_price_per_dwelling) missing.push('expected sale price per dwelling');
    if (missing.length) {
      alert(`Please fill in: ${missing.join(', ')}.\n\nSale price per dwelling is the single most important input — without it the feasibility can't compute.`);
      return;
    }
    let feaso_preview = null;
    let score = null;
    if (state.sale_price_per_dwelling) {
      const libraryOverrides = getDefaultOverrides();
      const feaso = calculateFeaso({
        purchase_price: state.price,
        dwellings: state.target_dwellings,
        sale_price_per_dwelling: state.sale_price_per_dwelling,
        dwelling_m2: TIER2_DEFAULTS.dwelling_m2,
        cost_per_m2: TIER2_DEFAULTS.cost_per_m2,
        overrides: libraryOverrides,
      });
      feaso_preview = {
        forecast_profit: feaso.forecast_profit,
        profit_on_cost_pct: feaso.profit_on_cost_pct,
        net_sales: feaso.sales.net_sales,
      };
      score = scoreListing({
        listing: { price: state.price, land_m2: state.land_m2, frontage_m: state.frontage_m },
        parcel: parcelShape(),
        feaso: { profit_on_cost_pct: feaso.profit_on_cost_pct },
        targetDwellings: state.target_dwellings,
      });
    }
    const saved = saveListing({
      source: state.scrape?.source ?? 'manual',
      address: state.address,
      url: state.url || null,
      price: state.price,
      land_m2: state.land_m2 ?? null,
      frontage_m: state.frontage_m ?? null,
      parcel: parcelShape(),
      sappa: state.sappa,
      scrape: state.scrape,
      target_dwellings: state.target_dwellings,
      sale_price_per_dwelling: state.sale_price_per_dwelling ?? null,
      notes: state.notes,
      feaso_preview,
      score,
    });
    navigate(`/finder/${saved.id}`);
  };

  renderForm();
  recompute();

  return h('div', { class: 'max-w-[1200px] mx-auto px-6 md:px-10' },
    h('header', { class: 'pt-10 pb-6 border-b border-[color:var(--color-rule)] flex items-end justify-between gap-6 flex-wrap' },
      h('div', null,
        h('div', { class: 'folio' }, 'Finder · Capture'),
        h('h1', { class: 'display display-md mt-1' }, 'Paste the details.'),
      ),
      h('a', { href: '#/finder', class: 'btn btn-ghost' }, '← Back to finder'),
    ),
    h('div', { class: 'grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-10 md:gap-14 py-8' },
      formEl,
      preview,
    ),
  );
}

function formBody(state, onChange, onLookup, onScrapeUrl) {
  return [
    section('Paste a listing',
      urlPasteField(state, onScrapeUrl),
      scrapeSummary(state),
    ),
    section('Listing',
      addressField(state, onChange, onLookup),
      sappaSummary(state),
    ),
    section('The deal',
      twoUp(
        moneyField('Asking price', state.price, (v) => onChange({ price: v }), { required: true }),
        intField('Target dwellings', state.target_dwellings, (v) => onChange({ target_dwellings: v }), { min: 1, max: 8 }),
      ),
      moneyField('Expected sale price / dwelling', state.sale_price_per_dwelling, (v) => onChange({ sale_price_per_dwelling: v }), { required: true }),
    ),
    section('The block',
      twoUp(
        intField('Land (m²)', state.land_m2, (v) => onChange({ land_m2: v }), { min: 100, step: 'any' }),
        intField('Frontage (m)', state.frontage_m, (v) => onChange({ frontage_m: v }), { min: 5, step: 'any' }),
      ),
      twoUp(
        textField('Zone', state.zone, (v) => onChange({ zone: v }), 'e.g. General Neighbourhood'),
        checkboxField('Corner block', state.is_corner, (v) => onChange({ is_corner: v })),
      ),
    ),
    section('Notes',
      h('label', { class: 'block' },
        h('span', { class: 'field-label' }, 'Free-form'),
        h('textarea', {
          class: 'field min-h-[5rem]',
          placeholder: 'Anything worth remembering about this site.',
          oninput: (e) => onChange({ notes: e.target.value }),
        }, state.notes || ''),
      ),
    ),
    h('div', { class: 'pt-4 border-t border-[color:var(--color-rule)] flex gap-2' },
      h('button', { type: 'submit', class: 'btn btn-ink' }, 'Add to finder'),
      h('a', { href: '#/finder', class: 'btn btn-ghost' }, 'Cancel'),
    ),
  ];
}

function urlPasteField(state, onScrapeUrl) {
  const loading = state.scrape_state === 'loading';
  return h('div', { class: 'block' },
    h('span', { class: 'field-label flex items-center justify-between' },
      h('span', null, 'Listing URL'),
      h('span', { class: 'text-[color:var(--color-ink-faint)] normal-case tracking-normal' },
        'Domain works · REA often blocks',
      ),
    ),
    h('div', { class: 'flex gap-2' },
      h('input', {
        type: 'url',
        class: 'field flex-1',
        value: state.url ?? '',
        placeholder: 'https://www.domain.com.au/… or realestate.com.au/…',
        disabled: loading,
        onpaste: (e) => {
          const pasted = (e.clipboardData || window.clipboardData)?.getData('text');
          if (pasted?.match(/^https?:\/\/(www\.)?(domain|realestate)\.com\.au/i)) {
            e.preventDefault();
            setTimeout(() => onScrapeUrl(pasted), 0);
          }
        },
        onkeydown: (e) => {
          if (e.key === 'Enter') { e.preventDefault(); onScrapeUrl(e.target.value); }
        },
        onchange: (e) => { state.url = e.target.value; },
      }),
      h('button', {
        type: 'button',
        class: 'btn btn-ink',
        disabled: loading,
        onclick: () => onScrapeUrl(state.url),
      }, loading ? 'Fetching…' : 'Fetch →'),
    ),
  );
}

function scrapeSummary(state) {
  if (state.scrape_state === 'error') {
    return h('div', { class: 'text-[0.8rem] text-[color:var(--color-negative)] border-l-2 border-[color:var(--color-negative)] pl-3 py-1' },
      state.scrape_error || 'couldn\u2019t parse the listing — enter details manually',
    );
  }
  if (state.scrape_state !== 'done' || !state.scrape) return null;
  const s = state.scrape;
  const bits = [
    s.address,
    s.price_display,
    s.land_m2 ? `${s.land_m2} m²` : null,
    [s.bedrooms, s.bathrooms, s.carspaces].filter((x) => x != null).length
      ? `${s.bedrooms ?? '–'}×${s.bathrooms ?? '–'}×${s.carspaces ?? '–'}`
      : null,
  ].filter(Boolean);
  return h('div', { class: 'text-[0.8rem] text-[color:var(--color-ink-soft)] border-l-2 border-[color:var(--color-olive-500)] pl-3 py-1 space-y-1' },
    h('div', { class: 'folio text-[color:var(--color-olive-700)]' }, `From ${s.source}`),
    h('div', null, bits.join(' · ')),
    s.headline ? h('div', { class: 'text-[color:var(--color-ink-dim)] italic line-clamp-2' }, s.headline) : null,
  );
}

function addressField(state, onChange, onLookup) {
  const loading = state.sappa_state === 'loading';
  return h('div', { class: 'block' },
    h('span', { class: 'field-label flex items-center justify-between' },
      h('span', null, 'Address ', h('span', { class: 'text-[color:var(--color-negative)]' }, '*')),
      h('button', {
        type: 'button',
        class: 'text-[0.7rem] folio text-[color:var(--color-olive-700)] hover:text-[color:var(--color-ink)] disabled:opacity-50',
        disabled: loading || !state.address?.trim(),
        onclick: onLookup,
      }, loading ? 'Looking up…' : 'Look up SAPPA →'),
    ),
    h('input', {
      type: 'text',
      class: 'field',
      value: state.address ?? '',
      placeholder: 'e.g. 65 Bluehills Rd, O\u2019Halloran Hill SA 5158',
      required: true,
      oninput: (e) => {
        state.address = e.target.value;
        // Don't trigger full re-render on every keystroke — just update
        state.sappa = null;
        state.sappa_state = 'idle';
      },
      onkeydown: (e) => { if (e.key === 'Enter') { e.preventDefault(); onLookup(); } },
    }),
  );
}

function sappaSummary(state) {
  if (state.sappa_state === 'error') {
    return h('div', { class: 'text-[0.8rem] text-[color:var(--color-negative)] border-l-2 border-[color:var(--color-negative)] pl-3 py-1' },
      state.sappa_error,
    );
  }
  if (state.sappa_state !== 'done' || !state.sappa?.parcel) return null;
  const p = state.sappa.parcel;
  const z = state.sappa.zone;
  const os = state.sappa.overlay_summary ?? {};
  const overlays = state.sappa.overlays ?? [];
  const tnvs = state.sappa.tnvs ?? [];
  return h('div', { class: 'text-[0.8rem] text-[color:var(--color-ink-soft)] border-l-2 border-[color:var(--color-olive-500)] pl-3 py-1 space-y-0.5' },
    h('div', { class: 'folio text-[color:var(--color-olive-700)]' }, 'From SAPPA'),
    z ? h('div', null, h('span', { class: 'text-[color:var(--color-ink-faint)]' }, 'Zone · '), `${z.name}${z.code ? ` (${z.code})` : ''}`) : null,
    h('div', null,
      h('span', { class: 'text-[color:var(--color-ink-faint)]' }, 'Parcel · '),
      p.area_m2 ? `${Math.round(p.area_m2)} m²` : '—',
      p.distinct_road_count != null
        ? `, ${p.distinct_road_count} road${p.distinct_road_count === 1 ? '' : 's'} touching${p.is_corner ? ' — corner block' : ''}`
        : null,
    ),
    (os.min_site_per_dwelling || os.min_frontage || os.max_building_height_m || os.site_coverage_pct) ? h('div', null,
      h('span', { class: 'text-[color:var(--color-ink-faint)]' }, 'Variations · '),
      [
        os.min_site_per_dwelling ? `${os.min_site_per_dwelling} m² min site` : null,
        os.min_frontage ? `${os.min_frontage} m frontage` : null,
        os.max_building_height_m ? `${os.max_building_height_m} m height` : null,
        os.site_coverage_pct ? `${os.site_coverage_pct}% coverage` : null,
      ].filter(Boolean).join(' · '),
    ) : null,
    overlays.length ? h('div', null,
      h('span', { class: 'text-[color:var(--color-ink-faint)]' }, `Overlays (${overlays.length}) · `),
      overlays.map((o) => o.name).join(', '),
    ) : null,
    p.plan_parcel ? h('div', { class: 'text-[color:var(--color-ink-faint)] italic' }, `${p.plan_parcel}${p.title_ref ? ' · ' + p.title_ref : ''}`) : null,
  );
}

function section(title, ...children) {
  return h('section', null,
    h('div', { class: 'folio mb-4' }, title),
    h('div', { class: 'space-y-4' }, ...children),
  );
}

function twoUp(a, b) {
  return h('div', { class: 'grid grid-cols-2 gap-4' }, a, b);
}

// Field primitives (ledger-style)

function textField(label, value, onInput, placeholder, required) {
  return h('label', { class: 'block' },
    h('span', { class: 'field-label' }, label, required ? h('span', { class: 'text-[color:var(--color-negative)] ml-1' }, '*') : null),
    h('input', {
      type: 'text',
      class: 'field',
      value: value ?? '',
      placeholder: placeholder ?? '',
      required: !!required,
      oninput: (e) => onInput(e.target.value),
    }),
  );
}

function moneyField(label, value, onInput, options = {}) {
  const required = options.required === true;
  return h('label', { class: 'block' },
    h('span', { class: 'field-label' },
      label,
      required ? h('span', { class: 'text-[color:var(--color-negative)] ml-1' }, '*') : null,
    ),
    h('input', {
      type: 'text',
      inputmode: 'numeric',
      class: 'field',
      value: value == null ? '' : new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(value),
      placeholder: 'e.g. 780,000',
      oninput: (e) => {
        const raw = e.target.value.replace(/[^\d.-]/g, '');
        const n = raw === '' ? undefined : Number(raw);
        onInput(Number.isFinite(n) ? n : undefined);
      },
      onblur: (e) => {
        const raw = e.target.value.replace(/[^\d.-]/g, '');
        if (raw !== '') e.target.value = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(Number(raw));
      },
      onfocus: (e) => e.target.select(),
    }),
  );
}

function intField(label, value, onInput, opts = {}) {
  return h('label', { class: 'block' },
    h('span', { class: 'field-label' }, label),
    h('input', {
      type: 'number',
      class: 'field',
      value: value ?? '',
      min: opts.min,
      max: opts.max,
      step: opts.step ?? 1,
      oninput: (e) => {
        const v = e.target.value === '' ? undefined : Number(e.target.value);
        onInput(Number.isFinite(v) ? v : undefined);
      },
    }),
  );
}

function checkboxField(label, value, onChange) {
  return h('label', { class: 'flex items-end justify-between gap-3 pb-1 border-b border-[color:var(--color-rule)]' },
    h('span', { class: 'field-label mb-0' }, label),
    h('input', {
      type: 'checkbox',
      checked: !!value,
      class: 'h-4 w-4 accent-[color:var(--color-olive-700)]',
      onchange: (e) => onChange(e.target.checked),
    }),
  );
}

// Preview pane

function prePanel() {
  return h('div', { class: 'p-8 border border-[color:var(--color-rule)] text-[color:var(--color-ink-dim)] text-sm' },
    h('div', { class: 'folio mb-2' }, 'Live score'),
    h('p', null, 'Enter an asking price and expected sale price per dwelling to see the score.'),
  );
}

function scorePanel(s, feaso) {
  const score = s.score;
  const signal =
    score >= 70 ? 'text-[color:var(--color-positive)]'
    : score >= 40 ? 'text-[color:var(--color-warning)]'
    : 'text-[color:var(--color-negative)]';

  return h('div', { class: 'space-y-6' },
    h('div', { class: 'flex items-end justify-between gap-4 pb-4 border-b border-[color:var(--color-rule)]' },
      h('div', null,
        h('div', { class: 'folio' }, 'Live score'),
        h('div', { class: `display display-lg num ${signal}` }, String(score).padStart(2, '0'),
          h('span', { class: 'text-[color:var(--color-ink-faint)]' }, ' / ', s.maxScore),
        ),
      ),
      h('div', { class: 'text-right' },
        h('div', { class: 'folio' }, 'Profit · % cost'),
        h('div', { class: `num display display-sm ${signal}` }, money(feaso.forecast_profit)),
        h('div', { class: `num text-sm ${signal}` }, pct(feaso.profit_on_cost_pct)),
      ),
    ),

    h('ul', { class: 'space-y-1' },
      ...s.breakdown.map((b) => breakdownRow(b)),
    ),
  );
}

function breakdownRow(b) {
  const indicator = b.met === true ? '●' : b.met === false ? '○' : '—';
  const color =
    b.met === true ? 'text-[color:var(--color-positive)]'
    : b.met === false ? 'text-[color:var(--color-ink-faint)]'
    : 'text-[color:var(--color-ink-faint)]';

  return h('li', { class: 'leader py-1.5' },
    h('span', { class: 'min-w-0' },
      h('span', { class: `${color} mr-2` }, indicator),
      h('span', null, b.label),
      b.note ? h('span', { class: 'block text-[0.72rem] text-[color:var(--color-ink-faint)] italic ml-5' }, b.note) : null,
    ),
    h('span', { class: 'dots' }),
    h('span', { class: 'num shrink-0 display display-sm' }, `+${b.points}`),
  );
}
